import type {
    DraftCheck,
    FieldSource,
    RfiCheckCard,
    RfiDraftCard,
    RfiExtractCard,
} from "../../../api/rfiDrafts";
import { shortDate } from "../../../utils/time";

/**
 * The cards Marvin's RFI tools return (`MarvinAction.result`, CONTRACT_V31 §5.5), read by their
 * `kind`. Only the shape a presenter needs is checked: the server builds every label, value and
 * check, and the client shows them as they are.
 */
type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const hasFields = (card: UnknownRecord) =>
    Array.isArray(card.fields) && card.fields.every(isRecord);

/** A draft card. Its `origin` is not read here (an update of a draft Marvin did not make carries
 *  none), so a card without one still renders as a draft. */
export function asDraftCard(result: unknown): RfiDraftCard | null {
    if (!isRecord(result) || result.kind !== "rfi_draft") return null;
    if (typeof result.record_id !== "string" || typeof result.href !== "string") return null;
    if (!hasFields(result)) return null;
    return result as unknown as RfiDraftCard;
}

export function asCheckCard(result: unknown): RfiCheckCard | null {
    if (!isRecord(result) || result.kind !== "rfi_check") return null;
    if (typeof result.verdict !== "string" || !Array.isArray(result.checks)) return null;
    return result as unknown as RfiCheckCard;
}

/** What answers a check's question, when an earlier RFI does (`rfi_check`'s `answered_by`). */
export type AnsweredBy = RfiCheckCard["answered_by"];

/**
 * An extraction runs the check itself on the PDF's question (CONTRACT_V31 §4.5), and its card
 * carries that check's verdict and what answers it (the §11 F-16 flow), beyond §5.5's fields.
 */
export type RfiExtractCardWithCheck = RfiExtractCard & {
    verdict?: string | null;
    answered_by?: AnsweredBy;
};

export function asExtractCard(result: unknown): RfiExtractCardWithCheck | null {
    if (!isRecord(result) || result.kind !== "rfi_extract") return null;
    if (!isRecord(result.file) || typeof result.file.id !== "string") return null;
    if (!hasFields(result) || !Array.isArray(result.checks)) return null;
    return result as unknown as RfiExtractCardWithCheck;
}

/** What "Draft one anyway" sends: Marvin drafts an RFI that asks only what is left open. */
export const DRAFT_ANYWAY_MESSAGE = "Draft one anyway";

/**
 * Why Marvin made no draft, after "No draft yet." (mock DrawingAnswered), in the words of the
 * check's verdict. An RFI is named only when the server says it answers the question
 * (`answered_by`), or, for `already_asked`, the RFI the check builds on; `answered` without one
 * means the documents answer it (CONTRACT_V31 §11 F-03/F-05), never an RFI. Null for every other
 * verdict: an RFI is needed (the draft card that follows carries the check), Marvin needs a
 * detail first (his question follows) or the check could not finish (his reply says so).
 */
export function noDraftReason(
    verdict: string | null | undefined,
    answeredBy: AnsweredBy | null | undefined,
    buildsOn: readonly { label: string }[] = [],
): string | null {
    if (verdict !== "already_asked" && verdict !== "answered") return null;
    if (answeredBy) {
        const when = answeredBy.answered_on ? ` on ${shortDate(answeredBy.answered_on)}` : "";
        return `${answeredBy.label} answered this${when}.`;
    }
    if (verdict === "answered") return "The documents answer this.";
    return `${buildsOn[0]?.label ?? "An earlier RFI"} already asks this.`;
}

/** A source Marvin could not stand behind: shown in the warning colour. */
export const isWeakSource = (source: FieldSource | null | undefined): boolean =>
    source?.kind === "not_stated" || source?.kind === "unresolved";

/** A field value located in the PDF: its page and box. */
export interface LocatedSource {
    page: number;
    box: [number, number, number, number];
}

export const locatedSource = (source: FieldSource | null | undefined): LocatedSource | null =>
    source?.kind === "pdf" && typeof source.page === "number" && Array.isArray(source.box)
        ? { page: source.page, box: source.box }
        : null;

const boxKey = ({ page, box }: LocatedSource) => `${page}:${box.join(",")}`;

/**
 * The numbered boxes of an extraction: each distinct located source gets the next number, in
 * field order, so two fields read from the same words share one number (the mock's "1" on both
 * Received from and Responsible subcontractor).
 */
export function sourceNumbers(card: RfiExtractCard): {
    numberOf: (field: string) => number | null;
    boxes: { n: number; source: LocatedSource }[];
} {
    const byBox = new Map<string, number>();
    const byField = new Map<string, number>();
    const boxes: { n: number; source: LocatedSource }[] = [];
    for (const field of card.fields) {
        const located = locatedSource(field.source);
        if (!located) continue;
        const key = boxKey(located);
        let n = byBox.get(key);
        if (n === undefined) {
            n = boxes.length + 1;
            byBox.set(key, n);
            boxes.push({ n, source: located });
        }
        byField.set(field.key, n);
    }
    return { numberOf: (field) => byField.get(field) ?? null, boxes };
}

/** The check lines' order stays the server's; this only names the tone of each state. */
export const checkTone = (state: DraftCheck["state"]): "success" | "neutral" | "warning" =>
    state === "ok" ? "success" : state === "warn" ? "warning" : "neutral";

/** A line that starts a list item ("1.", "2)", "-", "•"): it keeps its own line. */
const LIST_ITEM = /^(\d+[.)]|[-•*])\s/;
/** A line that ends a sentence (a closing quote or bracket may follow the stop). */
const SENTENCE_END = /[.?!:;]["'”’)\]]?$/;
/** How far short of the block's longest line a sentence's last line ends to close a paragraph. */
const SHORT_LINE = 0.85;

/**
 * Text read from a PDF, reflowed: the PDF's hard line breaks become spaces (a line that ends in a
 * hyphen joins the next with no space, so "fire-\nrated" reads "fire-rated"; RFIs are rarely
 * typeset with soft hyphens, and a compound split at its hyphen is common), while real paragraph
 * breaks stay, as a blank line; a list stays one item to a line, under its lead-in. A break is
 * real where the PDF leaves a blank line, before a list item, and after a line that ends a sentence
 * well short of the block's longest line (a paragraph's last line; the extraction flattens the
 * PDF's paragraph gap to a single break, "…card reader.\nPlease advise…").
 */
export function reflowParagraphs(text: string): string {
    const blocks = text.replace(/\r\n?/g, "\n").split(/\n[ \t]*\n+/);
    return blocks
        .map((block) => {
            const lines = block
                .split("\n")
                .map((line) => line.trim())
                .filter(Boolean);
            const longest = Math.max(0, ...lines.map((line) => line.length));
            const paragraphs: { text: string; item: boolean }[] = [];
            let current: { text: string; item: boolean } | null = null;
            lines.forEach((line, index) => {
                const item = LIST_ITEM.test(line);
                if (!current || item) {
                    current = { text: line, item };
                    paragraphs.push(current);
                } else if (current.text.endsWith("-") && /^[a-z]/.test(line)) {
                    current.text += line;
                } else {
                    current.text = `${current.text} ${line}`;
                }
                const next = lines[index + 1];
                const ends = SENTENCE_END.test(line) && line.length < longest * SHORT_LINE;
                if (ends || (next !== undefined && LIST_ITEM.test(next))) current = null;
            });
            // List items stay one to a line, under their lead-in ("Options:"); a paragraph is
            // set apart from its neighbours.
            return paragraphs.reduce((joined, paragraph, index) => {
                if (index === 0) return paragraph.text;
                const before = paragraphs[index - 1];
                const listed = paragraph.item && (before.item || before.text.endsWith(":"));
                return `${joined}${listed ? "\n" : "\n\n"}${paragraph.text}`;
            }, "");
        })
        .filter(Boolean)
        .join("\n\n");
}
